import { cx } from './utils';

/**
 * The one button vocabulary shared by the toolbar's `actions` and
 * `buttonColumn`'s per-row buttons, so a "Delete" in the toolbar and a
 * "Delete" in a row can't drift apart.
 *
 * Size owns padding and radius, variant owns colour only. That split is
 * deliberate: two same-specificity Tailwind utilities on one element resolve by
 * stylesheet order, not class order, so a variant that also carried padding
 * would fight the size class non-deterministically.
 */
export type DataTableButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
export type DataTableButtonSize = 'sm' | 'md';

const BASE =
    'inline-flex items-center justify-center gap-1.5 font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed';

const SIZE: Record<DataTableButtonSize, string> = {
    /** Row-level: matches the design system's action-required row buttons. */
    sm: 'h-8 px-3 text-sm rounded-lg',
    /** Toolbar-level. */
    md: 'px-4 py-2 text-sm rounded-xl'
};

const VARIANT: Record<DataTableButtonVariant, string> = {
    primary: 'bg-black text-white shadow hover:bg-gray-900',
    secondary: 'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50',
    danger: 'bg-white text-red-800 border border-red-800 hover:bg-red-50',
    ghost: 'text-gray-700 hover:bg-gray-100'
};

export function dataTableButtonClasses(
    variant: DataTableButtonVariant = 'secondary',
    size: DataTableButtonSize = 'md',
    className?: string
): string {
    return cx(BASE, SIZE[size], VARIANT[variant], className);
}
